// Shared auth-page chrome — CHG-1277 / ITER-3402.
//
// Brand-aware centered card layout used by the 5 v2 auth pages
// (ResetPassword / VerifyEmail / AcceptInvite / SsoCallback /
// RegisterFromEmail). Lives OUTSIDE <AppShell> — these are pre-auth
// public surfaces. Pattern lifted from _dev/auth-preview.tsx (CHG-1271)
// per the brief's "single brand-aware product" wireframe consistency
// requirement.
//
// useBrand() integration is the contract: page consumers receive
// `chrome.login_bg_color` / `chrome.logo_url` / `chrome.wordmark` /
// `chrome.tagline` / `chrome.display_name`. When variantId !== "_default"
// the "Powered by Alora" footer renders per the brief.

import type { ReactNode } from "react";
import { useBrand } from "../../contexts/BrandContext";

interface AuthLayoutProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** data-page attribute for test selection */
  pageId: string;
}

export function AuthLayout({ title, subtitle, children, pageId }: AuthLayoutProps) {
  const { variantId, chrome } = useBrand();
  const showAloraFooter = variantId !== "_default";

  return (
    <main
      data-auth-page={pageId}
      style={{
        minHeight: "100vh",
        background: chrome.login_bg_color,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        data-card="auth"
        style={{
          background: "var(--color-surface)",
          borderRadius: "var(--radius-lg, 12px)",
          boxShadow: "var(--shadow-medium)",
          padding: "40px 32px",
          width: "100%",
          maxWidth: 420,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 16,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
          }}
        >
          <img
            src={chrome.logo_url}
            alt={`${chrome.display_name} logo`}
            data-brand-logo
            style={{ width: 56, height: 56, objectFit: "contain" }}
          />
          <h1
            className="font-wordmark"
            data-brand-wordmark
            style={{
              fontSize: 22,
              fontWeight: 600,
              margin: 0,
              color: "var(--color-text)",
              fontFamily: "var(--font-wordmark, 'Poppins', sans-serif)",
            }}
          >
            {chrome.wordmark}
          </h1>
        </div>

        <div style={{ textAlign: "center" }}>
          <h2 style={{ fontSize: 18, fontWeight: 500, margin: 0, color: "var(--color-text)" }}>
            {title}
          </h2>
          {subtitle && (
            <p
              style={{
                marginTop: 6,
                marginBottom: 0,
                fontSize: 13,
                color: "var(--color-text-muted)",
              }}
            >
              {subtitle}
            </p>
          )}
        </div>

        {children}
      </div>

      {showAloraFooter && (
        <div
          data-powered-by="alora"
          style={{
            marginTop: 16,
            fontSize: 11,
            color: "var(--color-text-muted)",
          }}
        >
          Powered by Alora
        </div>
      )}
    </main>
  );
}
